import i18next, { TFunction } from 'i18next'
import moment from '@e3dc-react/shell/Libs/moment'
import { ChargingSessionModelV1 } from 'src/Libs/e-mobility/charging-sessions'
import { ChargingSessionCsvDataModel, ChargingSessionCsvHeadersModel } from 'src/Libs/e-mobility/charging-sessions/models/charging-session.model'
import { isEmpty } from 'lodash'

type ChargedSessionProps = {
  energy: number
  staticPrice: number
  dynamicCost: number
  isDynamic: boolean
}

type DischargedSessionProps = {
  energy: number
  sessionPrice: number
}

interface SessionPriceAndCost {
  consumption: string | number
  price: string
  cost: string
}

const WAT_TO_KWH = 1000

/**
 * Function for generating csv file for export charging sessions from Dashboard
 * @param session array of ChargingSessionModel
 * @param t translation function from useCustomTranslations
 * @return generated csv headers and generated csv data
 */
export const generateChargingSessionsCsv = (
  sessions: ChargingSessionModelV1[],
  t: TFunction
): { csvData: ChargingSessionCsvDataModel[]; csvHeaders: ChargingSessionCsvHeadersModel[] } => {
  const csvData = sessions.map(session => {
    const wallboxName = generateWallboxName(session.wallbox.name, session.wallbox.id, t)
    const authentication = generateAuthentication(session.authentication, t)

    const {
      consumption: chargedConsumption,
      cost: chargedCost,
      price: chargedPrice,
    } = calculateSessionConsumptionAndPrice({
      energy: session.energyAll,
      staticPrice: session.chargePrice,
      dynamicCost: session.chargePriceTotal,
      isDynamic: session.dynamic,
    })

    const {
      consumption: dischargedConsumption,
      cost: dischargedCost,
      price: dischargedPrice,
    } = calculateDischargedSessionConsumptionAndPrice({ energy: session.energyDischarged, sessionPrice: session.dischargePrice })

    return {
      timePeriod: `${moment(session.startAt).format(t('common.format.dateTime'))} - ${moment(session.stopAt).format(t('common.format.dateTime'))}`,
      wallbox: wallboxName,
      authStatus: authentication,
      chargedPriceKwh: chargedPrice,
      chargeConsumption: t('common.format.kwh', { value: chargedConsumption }),
      chargeCosts: chargedCost,
      dischargePriceKwh: dischargedPrice,
      dischargeConsumption: t('common.format.kwh', { value: dischargedConsumption }),
      dischargeCosts: dischargedCost,
    }
  })

  const csvHeaderValues = isEmpty(csvData) ? [] : Object.keys(csvData[0])
  const csvHeaders = csvHeaderValues.map(value => {
    return { label: t(`emobility.chargingSession.export-${value}`), key: value as keyof ChargingSessionCsvDataModel }
  })

  return { csvData, csvHeaders }
}

/**
 * Function for format wallbox name or id.
 * Returns formatted wallbox name as string
 */
const generateWallboxName = (wallboxName: string | undefined, wallboxId: string, t: TFunction): string => {
  if (wallboxName) {
    return t('emobility.chargingSession.wallboxNameAndId', { wallboxName: wallboxName, wallboxId: wallboxId })
  }

  return t('emobility.chargingSession.wallboxId', { id: wallboxId })
}

/**
 * Function for format authentication type.
 * Returns formatted authentication type
 */
const generateAuthentication = (authentication: ChargingSessionModelV1['authentication'], t: TFunction): string => {
  if (authentication.type === 'NONE') {
    return t('emobility.chargingSession.noAuthentication')
  }

  if (authentication.type === 'RFID' || authentication.type === 'rfid') {
    return t(`emobility.chargingSession.export-auth-type`, { rfid: authentication.rfid })
  }
  return t(`emobility.chargingSession.authType.${authentication.type}`)
}

/**
 * Function for calculate consumption and price per single session for discharged values
 * Returns formatted consumption, cost and valid price
 */
const calculateDischargedSessionConsumptionAndPrice = ({ energy, sessionPrice }: DischargedSessionProps): SessionPriceAndCost => {
  const consumption = energy ? energy / WAT_TO_KWH : 0
  const formattedConsumption = consumption ? consumption.toLocaleString(i18next.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 0
  const cost = new Intl.NumberFormat(i18next.language, { style: 'currency', currency: 'EUR' }).format(sessionPrice * consumption)
  const price = new Intl.NumberFormat(i18next.language, { style: 'currency', currency: 'EUR' }).format(sessionPrice)

  return {
    consumption: formattedConsumption,
    cost,
    price,
  }
}

/**
 * Function for calculate consumption and price per single session for static and dynamic prices and costs
 * From E3/DC BE, we are receiving cost instead of price when session is started with dynamic tariff.
 * Price for dynamic session needs to be calculated on FE - session cost divided with consumption
 * If consumption or session cost for dynamic session is 0, then price will be displayed as 0.
 * For static prices we are getting it from wallboxes and calculate price and cost regular.
 * @param energy - total energy used in charging session as number
 * @param staticPrice - price of static tariff as number
 * @param dynamicCost - cost of dynamic session as number
 * @param isDynamic - is session dynamic or not as boolean
 */
const calculateSessionConsumptionAndPrice = ({ energy, staticPrice, dynamicCost, isDynamic }: ChargedSessionProps): SessionPriceAndCost => {
  const consumption = energy ? energy / WAT_TO_KWH : 0
  const formattedConsumption = consumption ? consumption.toLocaleString(i18next.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 0

  const formattedStaticCost = new Intl.NumberFormat(i18next.language, { style: 'currency', currency: 'EUR' }).format(staticPrice * consumption)
  let formattedStaticPrice: string

  if (!isDynamic && consumption && staticPrice) {
    formattedStaticPrice = new Intl.NumberFormat(i18next.language, { style: 'currency', currency: 'EUR' }).format(staticPrice)
  } else {
    formattedStaticPrice = new Intl.NumberFormat(i18next.language, { style: 'currency', currency: 'EUR' }).format(0)
  }

  const formattedDynamicCost = new Intl.NumberFormat(i18next.language, { style: 'currency', currency: 'EUR' }).format(dynamicCost)
  let formattedDynamicPrice: string

  if (isDynamic && consumption && dynamicCost) {
    formattedDynamicPrice = new Intl.NumberFormat(i18next.language, { style: 'currency', currency: 'EUR' }).format(dynamicCost / Number(consumption))
  } else {
    formattedDynamicPrice = new Intl.NumberFormat(i18next.language, { style: 'currency', currency: 'EUR' }).format(0)
  }

  return {
    consumption: formattedConsumption,
    cost: isDynamic ? formattedDynamicCost : formattedStaticCost,
    price: isDynamic ? formattedDynamicPrice : formattedStaticPrice,
  }
}
